import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import { useRecoilValue, useRecoilState } from 'recoil';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import {
  DatePicker,
  Tabs,
  Table,
  Spin,
  Tag,
  Collapse,
  Divider,
  Segmented,
  Popover
} from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/pro-duotone-svg-icons';
import Button from '../../shared-components/buttons/Button';
import MainPage from '../../shared-components/main-page/MainPage';
import { H1 } from '../../shared-components/typography/Title';
import { userPrivilagesAtom, notificationsAtom, reloadSpecialServicesAtom } from '../../../atoms/Atoms';
import api from '../../../api/api';
import Select from '../../shared-components/form/Select';
import envConfig from '../../../envConfig';
import { thousand, toLocalTime } from '../../../functions';
import AntdInput from '../../shared-components/form/AntdInput';
import Input from '../../shared-components/form/Input';

function TransdevTrips() {
  const { t, i18n } = useTranslation();
  const { RangePicker } = DatePicker;
  const userPrivilages = useRecoilValue(userPrivilagesAtom);
  const [notifications, setNotifications] = useRecoilState(notificationsAtom);
  const [state, setState] = useState({
    start: null,
    end: null,
    selectedRouteIds: [''],
    selectedBusIds: [''],
    selectedSubSystems: [''],
    searchQuery: '',
    loading: false,
    page: 0,
    size: 20,
    totalCount: 0,
    trips: [],
  });
  const [routeState, setRouteState] = useState({
    routes: [],
    loading: false
  });
  const [busState, setBusState] = useState({
    buses: [],
    loading: false,
  });
  const [subSytemState, setSubSytemState] = useState({
    subSytems: [],
    loading: false,
  });

  function getRoutes() {
    setRouteState({
      routes: [],
      loading: true
    });
    api.get('transdev/routes')
    .then((response) => {
      if (response.data.success) {
        setRouteState({
          routes: response.data.data,
          loading: false
        });
      } else {
        setRouteState({
          routes: [],
          loading: false
        });
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: response.data.error,
            error: true,
          }
        ]);
      }
    })
    .catch((error) => {
      setRouteState({
        routes: [],
        loading: false
      });
      setNotifications([
        ...notifications,
        {
          title: t('error'),
          description: error.message,
          error: true,
        }
      ]);
    })
  }

  function getSubSystems() {
    setSubSytemState({
      subSytems: [],
      loading: true
    });
    api.get('transdev/subsystems')
    .then((response) => {
      if (response.data.success) {
        setSubSytemState({
          subSytems: response.data.data,
          loading: false
        });
      } else {
        setSubSytemState({
            subSytems: [],
          loading: false
        });
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: response.data.error,
            error: true,
          }
        ]);
      }
    })
    .catch((error) => {
        setSubSytemState({
        subSytems: [],
        loading: false
      });
      setNotifications([
        ...notifications,
        {
          title: t('error'),
          description: error.message,
          error: true,
        }
      ]);
    })
  }

  function getBuses() {
    setBusState({
      buses: [],
      loading: true
    });
    api.get('transdev/buses')
    .then((response) => {
      if (response.data.success) {
        setBusState({
          buses: response.data.data,
          loading: false
        });
      } else {
        setBusState({
          buses: [],
          loading: false
        });
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: response.data.error,
            error: true,
          }
        ]);
      }
    })
    .catch((error) => {
        setBusState({
        buses: [],
        loading: false
      });
      setNotifications([
        ...notifications,
        {
          title: t('error'),
          description: error.message,
          error: true,
        }
      ]);
    })
  }

  function getData(page) {
    setState({
      ...state,
      page,
      loading: true
    });
    const startDate = dayjs(state.start);
    const endDate = dayjs(state.end);
    const startString = startDate.add(4, 'hour').format('YYYY-MM-DDTHH:mm:ss');
    const endString = endDate.add(4, 'hour').add(1, 'day').add(-1, 'second').format('YYYY-MM-DDTHH:mm:ss');
    api.post('transdev/trips/search', {
        ...state,
      BusIds: _.filter(state.selectedBusIds, (i) => i !== ''),
      RouteIds: _.filter(state.selectedRouteIds, (i) => i !== ''),
      SubSystems: _.filter(state.selectedSubSystems, (i) => i !== ''),
      startDate: startString,
      SearchQuery: state.searchQuery,
      endDate: endString,
      page,
      size: state.size,
    })
    .then((response) => {
      if (response.data.success) {
        setState({
          ...state,
          loading: false,
          totalCount: response.data.data.total,
          trips: response.data.data.trips,
        });
      } else {
        setState({
          ...state,
          loading: false
        });
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: response.data.error,
            error: true,
          }
        ]);
      }
    })
    .catch((error) => {
      setState({
        ...state,
        loading: false
      });
      setNotifications([
        ...notifications,
        {
          title: t('error'),
          description: error.message,
          error: true,
        }
      ]);
    })
  }

  function convertSecondToMinutes(seconds) {
    const minutes = Math.floor(seconds / 60);
    const leftOverSeconds = seconds % 60;
    let resultText = `${minutes} ${t('minutes')}`;
    if (leftOverSeconds > 0) {
        resultText += ` ${leftOverSeconds} ${t('seconds')}`;
    }
    return resultText;
  }

  useEffect(() => {
    setState({
      ...state,
      start: dayjs().startOf('month').format('MMM DD, YYYY'),
      end: dayjs().endOf('month').format('MMM DD, YYYY'),
      size: 20,
      page: 0,
      selectedRouteIds: [''],
      selectedBusIds: [''],
      selectedSubSystems: [''],
      searchQuery: '',
      loading: false,
      totalCount: 0,
      trips: [],
    });
    getSubSystems();
    getRoutes();
    getBuses();
  }, []);

  useEffect(() => {
    if (state?.start && state?.end) {
      getData(0);
    }
  }, [state?.start, state?.end, state?.size]);

  const tripsColumns = [
    {
      title: '',
      align: 'center',
      className: 'text-sm md:hidden',
      render: (row) => (
        <div className="grid gap-2 grid-cols-6">
          <div className="col-span-2">
            <p className="font-bold">{t('subsystem')}</p>
            <p>{row.subSystem}</p>
          </div>
          <div className="col-span-2">
            <p className="font-bold">{t('route')}</p>
            <p>{row.routeId}</p>
          </div>
          <div className="col-span-2">
            <p className="font-bold">{t('vehicle')}</p>
            <p>{row.wkId ? row.wkId : row.vehicleId}</p>
          </div>

          <div className="col-span-3 my-2">
            <p className="font-bold">{t('start')}</p>
            <p>{row.startLocation}</p>
            <p>{dayjs(row.tripStart).format('MMM DD, YYYY hh:mm A')}</p>
          </div>
          <div className="col-span-3 my-2">
            <p className="font-bold">{t('end')}</p>
            <p>{row.endLocation}</p>
            <p>{dayjs(row.tripEnd).format('MMM DD, YYYY hh:mm A')}</p>
          </div>
          
          <div className="col-span-2">
            <p className="font-bold">{t('pattern')}</p>
            <p>{row.patternId}</p>
          </div>
          <div className="col-span-2">
            <p className="font-bold">{t('revenue_miles')}</p>
            <p>{thousand(row.revenueMiles, 2)}</p>
          </div>
          <div className="col-span-2">
            <p className="font-bold">{t('revenue_hours')}</p>
            <p>{thousand(row.revenueHours, 2)}</p>
            <p>({convertSecondToMinutes(row.revenueSeconds)})</p>
          </div>
        </div>
      )
    },
    {
      title: t('subsystem'),
      align: 'center',
      className: 'text-sm hidden md:table-cell',
      render: (row) => (
        <div>
          <p>{row.subSystem}</p>
        </div>
      ),
    },
    {
      title: t('route'),
      align: 'center',
      className: 'text-sm hidden md:table-cell',
      render: (row) => (
        <div>
          <p>{row.routeId}</p>
        </div>
      ),
    },
    {
      title: t('vehicle'),
      align: 'center',
      className: 'text-sm hidden md:table-cell',
      render: (row) => (
        <div>
          <p>{row.wkId ? row.wkId : row.vehicleId}</p>
        </div>
      ),
    },
    {
      title: t('start'),
      align: 'center',
      className: 'text-sm hidden md:table-cell',
      render: (row) => (
        <div>
          <p>{row.startLocation} - {dayjs(row.tripStart).format('MMM DD, YYYY hh:mm A')}</p>
        </div>
      ),
    },
    {
      title: t('end'),
      align: 'center',
      className: 'text-sm hidden md:table-cell',
      render: (row) => (
        <div>
          <p>{row.endLocation} - {dayjs(row.tripEnd).format('MMM DD, YYYY hh:mm A')}</p>
        </div>
      ),
    },
    {
      title: t('pattern'),
      align: 'center',
      className: 'text-sm hidden md:table-cell',
      render: (row) => (
        <div>
          <p>{row.patternId}</p>
        </div>
      ),
    },
    {
      title: t('revenue_miles'),
      align: 'center',
      className: 'text-sm hidden md:table-cell',
      render: (row) => (
        <div>
          <p>{thousand(row.revenueMiles, 2)}</p>
        </div>
      ),
    },
    {
      title: t('revenue_hours'),
      align: 'center',
      className: 'text-sm hidden md:table-cell',
      render: (row) => (
        <div>
          <p>{thousand(row.revenueHours, 2)}</p>
          <p>({convertSecondToMinutes(row.revenueSeconds)})</p>
        </div>
      ),
    },
  ];

  return (
    <MainPage>
      <H1 text={`Transdev - ${t('trips')}`} />
      <div className="grid gap-2 grid-cols-1 md:grid-cols-2 lg:grid-cols-6">
        <Select
          className="col-span-1 lg:col-start-2"
          label={t('subsystem')}
          id="selectedSubSystems"
          name="selectedSubSystems"
          value={state.selectedSubSystems}
          multiple
          allowClear
          onChange={(value) => {
            if (value[value.length - 1] === '') {
              setState({
                ...state,
                selectedSubSystems: ['']
              });
            } else {
              const check = _.filter(value, (p) => p !== '');
              if (value.length === 0) {
                setState({
                  ...state,
                  selectedSubSystems: ['']
                });
              } else if (check.length > 0) {
                setState({
                  ...state,
                  selectedSubSystems: check
                });
              } else {
                setState({
                  ...state,
                  selectedSubSystems: value
                });
              }
            }
          }}
          loading={subSytemState.loading}
          disabled={subSytemState.loading || state.loading}
          options={[{ key: 'any', value: '', text: t('any') }, ..._.map(subSytemState.subSytems, (s) => ({ key: s, value: s, text: s }))]}
        />
        <Select
          className="col-span-1"
          label={t('route')}
          id="selectedRouteIds"
          name="selectedRouteIds"
          value={state.selectedRouteIds}
          multiple
          allowClear
          onChange={(value) => {
            if (value[value.length - 1] === '') {
              setState({
                ...state,
                selectedRouteIds: ['']
              });
            } else {
              const check = _.filter(value, (p) => p !== '');
              if (value.length === 0) {
                setState({
                  ...state,
                  selectedRouteIds: ['']
                });
              } else if (check.length > 0) {
                setState({
                  ...state,
                  selectedRouteIds: check
                });
              } else {
                setState({
                  ...state,
                  selectedRouteIds: value
                });
              }
            }
          }}
          loading={routeState.loading}
          disabled={routeState.loading || state.loading}
          options={[{ key: 'any', value: '', text: t('any') }, ..._.map(routeState.routes, (route) => ({ key: route, value: route, text: route }))]}
        />
        <Select
          className="col-span-1"
          label={t('vehicle')}
          id="selectedBusIds"
          name="selectedBusIds"
          value={state.selectedBusIds}
          multiple
          allowClear
          onChange={(value) => {
            if (value[value.length - 1] === '') {
              setState({
                ...state,
                selectedBusIds: ['']
              });
            } else {
              const check = _.filter(value, (p) => p !== '');
              if (value.length === 0) {
                setState({
                  ...state,
                  selectedBusIds: ['']
                });
              } else if (check.length > 0) {
                setState({
                  ...state,
                  selectedBusIds: check
                });
              } else {
                setState({
                  ...state,
                  selectedBusIds: value
                });
              }
            }
          }}
          loading={busState.loading}
          disabled={busState.loading || state.loading}
          options={[{ key: 'any', value: '', text: t('any') }, ..._.map(busState.buses, (b) => ({ key: b, value: b, text: b }))]}
        />
        <RangePicker
          value={[
            dayjs(state?.start),
            dayjs(state?.end)
          ]}
          disabled={state.loading}
          className="h-11 bg-transparent col-span-1 md:mt-1"
          onChange={(obj) => {
            setState({
              ...state,
              start: obj[0],
              end: obj[1]
            });
          }}
          format="MMM DD, YYYY"
        />
        <Button
          className="bg-primaryColor px-2 col-span-1 hidden lg:block lg:col-start-6"
          textColor="text-primaryTextColor"
          loaderColor="bg-primaryTextColor"
          borderColor="border-primaryBorderColor"
          text={t('refresh')}
          loading={state.loading}
          onClick={() => getData()}
        />
        <Input
          className="col-span-1 md:col-span-2 lg:col-span-6"
          label={t('search')}
          type="text"
          name="searchQuery"
          id="searchQuery"
          value={state?.searchQuery}
          onKeyDown={(key) => {
            if (key.keyCode === 13) {
              getData(0);
            }
          }}
          onChange={(obj) => setState({
            ...state,
            searchQuery: obj.target.value,
          })}
        />
        <Button
          className="bg-primaryColor px-2 col-span-1 md:col-start-2 lg:hidden"
          textColor="text-primaryTextColor"
          loaderColor="bg-primaryTextColor"
          borderColor="border-primaryBorderColor"
          text={t('refresh')}
          loading={state.loading}
          onClick={() => getData()}
        />
      </div>
      <Table
        bordered
        dataSource={state.trips}
        loading={state.loading}
        rowKey="id"
        className="mt-2"
        locale={{
          emptyText: t('no_data')
        }}
        pagination={{
          pageSize: state.size,
          showSizeChanger: true,
          current: state.page + 1,
          total: state?.totalCount,
          onChange: (page, size) => {
            if (page - 1 !== state?.page) {
              getData(page);
            } else {
              setState({
                ...state,
                size
              });
            }
          }
        }}
        columns={tripsColumns}
      />
    </MainPage>
  )
}

export default TransdevTrips;
